import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Calendar, Users, AlertTriangle } from "lucide-react"

interface BoundDownOrderProps {
  boundDownOrder: {
    id: string
    meaning: string
    dateOfOrder: string
    duration: string
    bondAmount: number
    amountPerSurety: number
    totalSureties: number
    suretyConditions: string
    consequenceOnDefault: string
    summary: string
    effectiveFrom: string
    effectiveTo: string
  }
}

export function BoundDownOrder({ boundDownOrder }: BoundDownOrderProps) {
  // Calculate duration from effectiveFrom and effectiveTo dates
  const calculateDuration = () => {
    if (boundDownOrder.duration) {
      return boundDownOrder.duration;
    }

    if (boundDownOrder.effectiveFrom && boundDownOrder.effectiveTo) {
      const fromDate = new Date(boundDownOrder.effectiveFrom);
      const toDate = new Date(boundDownOrder.effectiveTo);

      // Calculate difference in milliseconds
      const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Convert to years, months, and days
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      const days = Math.floor((diffDays % 365) % 30);

      const parts = [];
      if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
      if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
      if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

      return parts.length > 0 ? parts.join(', ') : '-';
    }

    return '-';
  };

  return (
    <Card className="border border-gray-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-black flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Bound Down Order Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {boundDownOrder.meaning && (
          <div>
            <p className="text-sm font-medium text-gray-600">Meaning</p>
            <p className="text-black bg-gray-50 p-3 rounded-lg">{boundDownOrder.meaning}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Date of Order
            </p>
            <p className="text-gray-700 text-xs">{new Date(boundDownOrder.dateOfOrder).toLocaleDateString("en-IN")}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Duration</p>
            <Badge className="bg-blue-100 text-blue-800">{calculateDuration()}</Badge>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              Bond Amount
            </p>
            <p className="text-black text-sm font-semibold">₹{boundDownOrder.bondAmount?.toLocaleString() ?? "-"}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 hidden">
          <div>
            <p className="text-sm font-medium text-gray-600 flex items-center gap-1">
              <Users className="h-4 w-4" />
              Total Sureties
            </p>
            <p className="text-gray-700 text-xs">{boundDownOrder.totalSureties || '-'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Amount per Surety</p>
            <p className="text-black font-semibold">₹{boundDownOrder.amountPerSurety?.toLocaleString() ?? "-"}</p>
          </div>
        </div>

        {boundDownOrder.suretyConditions && (
          <div>
            <p className="text-sm font-medium text-gray-600">Surety Conditions</p>
            <p className="text-black bg-gray-50 p-3 rounded-lg">{boundDownOrder.suretyConditions || '-'}</p>
          </div>
        )}

        {boundDownOrder.consequenceOnDefault && (
          <div>
            <p className="text-sm font-medium text-gray-600">Consequence on Default</p>
            <p className="text-black bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
              {boundDownOrder.consequenceOnDefault}
            </p>
          </div>
        )}

        {boundDownOrder.summary && (
          <div>
            <p className="text-sm font-medium text-gray-600">Summary</p>
            <p className="text-black bg-gray-50 p-3 rounded-lg">{boundDownOrder.summary}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-600">Effective From</p>
            <p className="text-gray-700 text-xs">{new Date(boundDownOrder.effectiveFrom).toLocaleDateString("en-IN")}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Effective To</p>
            <p className="text-gray-700 text-xs">{new Date(boundDownOrder.effectiveTo).toLocaleDateString("en-IN")}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
